import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { HrDepartment, HrJob, HrPosition, HrWorkerCategory } from '../hr.models';
import { HrService } from '../hr.service';
import { HrActionDialogComponent } from '../shared/hr-action-dialog.component';
import { HrListTabsComponent } from '../shared/hr-list-tabs.component';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';

@Component({
  selector: 'app-hr-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    ButtonModule,
    CheckboxModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent,
    HrActionDialogComponent,
    HrListTabsComponent,
    HrModuleNavComponent
  ],
  providers: [MessageService],
  templateUrl: './hr-setup.component.html',
  styleUrls: ['./hr-setup.component.scss']
})
export class HrSetupComponent implements OnInit {
  activeListTab = 'categories';
  activeDialog?: 'category' | 'department' | 'job' | 'position';
  private readonly hrService = inject(HrService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  departments: HrDepartment[] = [];
  jobs: HrJob[] = [];
  positions: HrPosition[] = [];
  workerCategories: HrWorkerCategory[] = [];

  departmentForm: HrDepartment = { active: true };
  jobForm: HrJob = { active: true };
  positionForm: HrPosition = { active: true };
  workerCategoryForm: HrWorkerCategory = { active: true };

  readonly canManageOrg = this.permissionService.hasPermission(app_permissions.hr.org.manage);

  get departmentOptions() {
    return this.departments.map(item => ({ label: item.departmentName || `Department ${item.departmentId}`, value: item.departmentId }));
  }

  get jobOptions() {
    return this.jobs.map(item => ({ label: item.jobName || `Job ${item.jobId}`, value: item.jobId }));
  }

  get categoryOptions() {
    return this.workerCategories
      .filter(item => item.active)
      .map(item => ({ label: item.categoryName || this.format(item.categoryCode), value: item.categoryCode }));
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.hrService.departments().subscribe(response => this.departments = response.data ?? []);
    this.hrService.jobs().subscribe(response => this.jobs = response.data ?? []);
    this.hrService.positions().subscribe(response => this.positions = response.data ?? []);
    this.hrService.workerCategories(false).subscribe(response => this.workerCategories = response.data ?? []);
  }

  saveDepartment(): void {
    if (!this.canManageOrg) return;
    this.hrService.saveDepartment(this.departmentForm).subscribe({
      next: () => {
        this.departmentForm = { active: true };
        this.activeDialog = undefined;
        this.load();
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Department saved.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to save department.' })
    });
  }

  saveJob(): void {
    if (!this.canManageOrg) return;
    this.hrService.saveJob(this.jobForm).subscribe({
      next: () => {
        this.jobForm = { active: true };
        this.activeDialog = undefined;
        this.load();
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Job saved.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to save job.' })
    });
  }

  savePosition(): void {
    if (!this.canManageOrg) return;
    this.hrService.savePosition(this.positionForm).subscribe({
      next: () => {
        this.positionForm = { active: true };
        this.activeDialog = undefined;
        this.load();
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Position saved.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to save position.' })
    });
  }

  saveWorkerCategory(): void {
    if (!this.canManageOrg) return;
    this.hrService.saveWorkerCategory(this.workerCategoryForm).subscribe({
      next: () => {
        this.workerCategoryForm = { active: true };
        this.activeDialog = undefined;
        this.load();
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Worker category saved.' });
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to save worker category.' })
    });
  }

  editDepartment(item: HrDepartment): void {
    if (!this.canManageOrg) return;
    this.departmentForm = { ...item };
    this.activeDialog = 'department';
  }

  editJob(item: HrJob): void {
    if (!this.canManageOrg) return;
    this.jobForm = { ...item };
    this.activeDialog = 'job';
  }

  editPosition(item: HrPosition): void {
    if (!this.canManageOrg) return;
    this.positionForm = { ...item };
    this.activeDialog = 'position';
  }

  editWorkerCategory(item: HrWorkerCategory): void {
    if (!this.canManageOrg) return;
    this.workerCategoryForm = { ...item };
    this.activeDialog = 'category';
  }

  openActionDialog(dialog: NonNullable<HrSetupComponent['activeDialog']>): void {
    this.activeDialog = dialog;
  }

  actionDialogVisible(dialog: NonNullable<HrSetupComponent['activeDialog']>): boolean {
    return this.activeDialog === dialog;
  }

  setActionDialogVisible(dialog: NonNullable<HrSetupComponent['activeDialog']>, visible: boolean): void {
    this.activeDialog = visible ? dialog : undefined;
  }

  format(value?: string): string {
    return (value || '-').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }
}
